import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Mail, Send, Loader2, CalendarClock } from 'lucide-react';
import api from '../../lib/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';

/**
 * Per-org config for the scheduled monthly "ESG snapshot" email that
 * auto-attaches the branded ESG PDF to leadership/investors.
 */
export default function EsgSnapshotCard({ accountId }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [recipients, setRecipients] = useState('');
  const [day, setDay] = useState(1);

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const { data } = await api.get('/dashboard/org/mongo/emissions/esg-schedule', { params: { account_id: accountId } });
      setCfg(data);
      setEnabled(!!data.enabled);
      setRecipients((data.recipients || []).join(', '));
      setDay(data.day_of_month || 1);
    } catch {
      /* silent — card just shows defaults */
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  const save = async (nextEnabled = enabled) => {
    setSaving(true);
    try {
      const list = recipients.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
      const { data } = await api.put('/dashboard/org/mongo/emissions/esg-schedule', {
        account_id: accountId, enabled: nextEnabled, recipients: list, day_of_month: Number(day) || 1,
      });
      setCfg(data);
      toast.success(nextEnabled ? 'Monthly ESG email scheduled' : 'Schedule saved');
    } catch {
      toast.error('Could not save the schedule');
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    const list = recipients.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
    if (!list.length) { toast.error('Add at least one recipient first.'); return; }
    setSending(true);
    try {
      await save(enabled); // persist current recipients before sending
      const { data } = await api.post('/dashboard/org/mongo/emissions/esg-schedule/send-now', null, { params: { account_id: accountId } });
      const r = data.result || {};
      if (r.sent > 0) toast.success(`ESG snapshot sent to ${r.sent} recipient(s)`);
      else if (r.suppressed) toast.message('Preview mode — email suppressed', { description: `PDF built (${r.pdf_bytes || 0} bytes). Real sends run in production.` });
      else toast.warning(`Not sent — ${(r.errors || []).join('; ') || 'email provider not configured'}`);
      await load();
    } catch {
      toast.error('Could not send the snapshot');
    } finally {
      setSending(false);
    }
  };

  const nextSend = cfg?.next_send_at ? new Date(cfg.next_send_at).toLocaleDateString() : null;
  const lastSent = cfg?.last_sent_at ? new Date(cfg.last_sent_at).toLocaleDateString() : null;

  return (
    <Card className="border-slate-200/70 shadow-none" data-testid="esg-snapshot-card">
      <CardContent className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <Mail className="h-4 w-4 text-emerald-600" /> Monthly ESG email
          </div>
          <span className="text-xs text-slate-500">Auto-send the branded ESG PDF to leadership every month.</span>
          <div className="ml-auto flex items-center gap-2">
            <Label htmlFor="esg-enabled" className="text-xs text-slate-600">{enabled ? 'On' : 'Off'}</Label>
            <Switch
              id="esg-enabled"
              checked={enabled}
              onCheckedChange={(v) => { setEnabled(v); save(v); }}
              disabled={loading || saving}
              data-testid="esg-snapshot-toggle"
            />
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-[1fr_150px]">
          <div>
            <Label className="text-xs">Recipients (comma or newline separated)</Label>
            <Textarea
              rows={2}
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              placeholder="cfo@company.com, sustainability@company.com"
              className="text-xs"
              data-testid="esg-snapshot-recipients"
            />
          </div>
          <div>
            <Label className="text-xs">Send on day of month</Label>
            <Input
              type="number" min={1} max={28} value={day}
              onChange={(e) => setDay(e.target.value)}
              data-testid="esg-snapshot-day"
            />
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => save(enabled)} disabled={saving} data-testid="esg-snapshot-save">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Save schedule
          </Button>
          <Button size="sm" className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" onClick={sendNow} disabled={sending} data-testid="esg-snapshot-send-now">
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send test now
          </Button>
          {(nextSend || lastSent) && (
            <span className="flex items-center gap-1 text-[11px] text-slate-500" data-testid="esg-snapshot-status">
              <CalendarClock className="h-3 w-3" />
              {enabled && nextSend ? `Next: ${nextSend}` : 'Scheduling off'}{lastSent ? ` · Last sent: ${lastSent}` : ''}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
